import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getTextField } from '@lender/utils';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import FormGeneratorRichTextEditor from 'app/shared/componentsRestyled/common/formGenerator/FormGeneratorRichTextEditor';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { object } from 'yup';

import useForm from 'hooks/useForm';

import { closeDialogAction, showMessageAction } from 'store/global/actions';
import {
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';

import { TaskInfoTestIds } from './TaskInfo.testIds';

const schema = object().shape({
  userNote: getTextField(),
});

const ReasonForm = ({ isLoading, setIsLoading, taskId, rejectTaskAction, formRef, formName }) => {
  const dispatch = useDispatch();

  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(formName));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(formName));
  const isPageLocked = useSelector(selectFormLockedPage(formName));

  const isDisabledByLock = isLoading || isPageLocked;

  const { form, isFormChanged, setSchema, validateForm, validationErrors, handleChange } = useForm({
    userNote: '',
  });

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      dispatch(rejectTaskAction({ taskId, ...form }))
        .then(() => {
          dispatch(
            showMessageAction({
              message: 'Task rejected',
              variant: 'success',
            }),
          );
          dispatch(closeDialogAction());
        })
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [dispatch, form, setIsLoading, validateForm, taskId, rejectTaskAction],
  );

  return (
    <ModalFormWrapper
      formRef={formRef}
      formTestId="reason-form"
      isFormChanged={isFormChanged}
      isSubmitting={isLoading}
      submitButtonName="Reject"
      onSubmit={onSubmit}
    >
      <FormGeneratorRichTextEditor
        fullWidth
        multiline
        required
        className="mb-24"
        data-testid={TaskInfoTestIds.REASON_FORM_USER_NOTE_INPUT}
        error={!!validationErrors.userNote}
        helperText={validationErrors.userNote}
        id="userNote"
        isHidden={hiddenFieldsMap.rejectionReason}
        label="Rejection reason"
        name="userNote"
        readOnly={isDisabledByLock || lockedFieldsMap.rejectionReason}
        rows={4}
        type="text"
        value={form.userNote}
        variant="outlined"
        onChange={handleChange}
      />
    </ModalFormWrapper>
  );
};

ReasonForm.propTypes = {
  taskId: PropTypes.string.isRequired,
  rejectTaskAction: PropTypes.func.isRequired,
  formName: PropTypes.string,
  ...WithLoadingPropTypes,
};

export default withLoading(ReasonForm);
